import CodeIcon from '@mui/icons-material/Code';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { Box, Divider, IconButton, TextField, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import { useSlate } from 'slate-react';
import styled from 'styled-components';

import type { BlockType, MarkType, ToolbarPreset } from './types';
import {
  isBlockActive,
  isLinkActive,
  isMarkActive,
  toggleBlock,
  toggleMark,
  unwrapLink,
  wrapLink,
} from './utils';

interface ToolbarProps {
  preset: ToolbarPreset;
  disabled?: boolean;
}

const ToolbarContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 12px;
  background: linear-gradient(180deg, #fafbfc 0%, #f5f6f8 100%);
  border-bottom: 1px solid rgba(59, 111, 217, 0.12);
  border-radius: 12px 12px 0 0;
`;

const ToolbarButton = styled(IconButton)<{ $active?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  color: ${({ $active }) => ($active ? '#3B6FD9' : '#6b7280')};
  background: ${({ $active }) => ($active ? 'rgba(59, 111, 217, 0.1)' : 'transparent')};
  transition: all 0.15s ease;

  &:hover {
    background: rgba(59, 111, 217, 0.08);
    color: #3B6FD9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    font-size: 18px;
  }
`;

const ToolbarDivider = styled(Divider)`
  height: 24px;
  margin: 0 6px;
`;

const LinkInputContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-top: 1px solid rgba(59, 111, 217, 0.08);
`;

// Mark button configuration
const MARK_ICONS: Record<MarkType, React.ReactNode> = {
  bold: <FormatBoldIcon />,
  italic: <FormatItalicIcon />,
  underline: <FormatUnderlinedIcon />,
  strikethrough: <FormatStrikethroughIcon />,
  code: <CodeIcon />,
};

const MARK_LABELS: Record<MarkType, string> = {
  bold: 'Bold (Ctrl+B)',
  italic: 'Italic (Ctrl+I)',
  underline: 'Underline (Ctrl+U)',
  strikethrough: 'Strikethrough',
  code: 'Inline Code',
};

// Block button configuration
const BLOCK_ICONS: Record<BlockType, React.ReactNode> = {
  'paragraph': null,
  'heading-one': <span style={{ fontWeight: 700, fontSize: 14 }}>H1</span>,
  'heading-two': <span style={{ fontWeight: 700, fontSize: 13 }}>H2</span>,
  'block-quote': <FormatQuoteIcon />,
  'code-block': <CodeIcon />,
  'bulleted-list': <FormatListBulletedIcon />,
  'numbered-list': <FormatListNumberedIcon />,
};

const BLOCK_LABELS: Record<BlockType, string> = {
  'paragraph': 'Paragraph',
  'heading-one': 'Heading 1',
  'heading-two': 'Heading 2',
  'block-quote': 'Quote Block',
  'code-block': 'Code Block',
  'bulleted-list': 'Bullet List',
  'numbered-list': 'Numbered List',
};

interface MarkButtonProps {
  format: MarkType;
  disabled?: boolean;
}

const MarkButton: React.FC<MarkButtonProps> = ({ format, disabled }) => {
  const editor = useSlate();
  const isActive = isMarkActive(editor, format);

  return (
    <Tooltip title={MARK_LABELS[format]} arrow>
      <span>
        <ToolbarButton
          $active={isActive}
          disabled={disabled}
          onMouseDown={(e) => {
            e.preventDefault();
            toggleMark(editor, format);
          }}
        >
          {MARK_ICONS[format]}
        </ToolbarButton>
      </span>
    </Tooltip>
  );
};

interface BlockButtonProps {
  format: BlockType;
  disabled?: boolean;
}

const BlockButton: React.FC<BlockButtonProps> = ({ format, disabled }) => {
  const editor = useSlate();
  const isActive = isBlockActive(editor, format);

  if (!BLOCK_ICONS[format]) return null;

  return (
    <Tooltip title={BLOCK_LABELS[format]} arrow>
      <span>
        <ToolbarButton
          $active={isActive}
          disabled={disabled}
          onMouseDown={(e) => {
            e.preventDefault();
            toggleBlock(editor, format);
          }}
        >
          {BLOCK_ICONS[format]}
        </ToolbarButton>
      </span>
    </Tooltip>
  );
};

interface LinkButtonProps {
  disabled?: boolean;
  onShowInput: () => void;
}

const LinkButton: React.FC<LinkButtonProps> = ({ disabled, onShowInput }) => {
  const editor = useSlate();
  const isActive = isLinkActive(editor);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isActive) {
      unwrapLink(editor);
    } else {
      onShowInput();
    }
  };

  return (
    <Tooltip title={isActive ? 'Remove Link' : 'Add Link'} arrow>
      <span>
        <ToolbarButton
          $active={isActive}
          disabled={disabled}
          onMouseDown={handleClick}
        >
          {isActive ? <LinkOffIcon /> : <LinkIcon />}
        </ToolbarButton>
      </span>
    </Tooltip>
  );
};

const Toolbar: React.FC<ToolbarProps> = ({ preset, disabled }) => {
  const editor = useSlate();
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const handleAddLink = () => {
    if (linkUrl.trim()) {
      let url = linkUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      wrapLink(editor, url);
      setLinkUrl('');
      setShowLinkInput(false);
    }
  };

  const hasMarks = preset.marks.length > 0;
  const hasBlocks = preset.blocks.length > 0;

  return (
    <>
      <ToolbarContainer>
        {/* Mark buttons */}
        {preset.marks.map((mark) => (
          <MarkButton key={mark} format={mark} disabled={disabled} />
        ))}

        {/* Divider between marks and blocks */}
        {hasMarks && hasBlocks && <ToolbarDivider orientation="vertical" />}

        {/* Block buttons */}
        {preset.blocks.map((block) => (
          <BlockButton key={block} format={block} disabled={disabled} />
        ))}

        {/* Divider before link */}
        {preset.enableLinks && (hasMarks || hasBlocks) && (
          <ToolbarDivider orientation="vertical" />
        )}

        {/* Link button */}
        {preset.enableLinks && (
          <LinkButton
            disabled={disabled}
            onShowInput={() => setShowLinkInput(true)}
          />
        )}
      </ToolbarContainer>

      {/* Link input */}
      {showLinkInput && (
        <LinkInputContainer>
          <TextField
            size="small"
            placeholder="Enter URL..."
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddLink();
              } else if (e.key === 'Escape') {
                setShowLinkInput(false);
                setLinkUrl('');
              }
            }}
            autoFocus
            sx={{
              'flex': 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '0.875rem',
              },
            }}
          />
          <ToolbarButton
            onClick={handleAddLink}
            disabled={!linkUrl.trim()}
          >
            <LinkIcon />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              setShowLinkInput(false);
              setLinkUrl('');
            }}
          >
            <LinkOffIcon />
          </ToolbarButton>
        </LinkInputContainer>
      )}
    </>
  );
};

export default Toolbar;
